import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {Event} event - The cart update event, dispatched by the form that added the item.
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open') || this.refs.dialog.open) return;

    // A refused add (422: sold out in this market, quantity limit) keeps the drawer shut so the error the
    // product form shows next to the add button stays in view. The drawer's items still re-render.
    if (/** @type {CartAddEvent} */ (event).detail?.data?.didError) return;

    this.showDialog();

    // Focus goes back to the add button on close. The browser does that when the button had focus; Safari
    // does not focus a clicked button, so name it here.
    if (!this.returnFocusTarget && event.target instanceof Element) {
      const addButton = event.target.querySelector('[ref="addToCartButton"]');
      if (addButton instanceof HTMLElement) this.returnFocusTarget = addButton;
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
